import React, { useState } from "react";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { InvoicePrintModal } from "./InvoicePrintModal.jsx";
import { PaymentTallyModal } from "./PaymentTallyModal.jsx";
import { PaymentReceiptModal } from "./PaymentReceiptModal.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { useBillPaymentCorrections } from "./BillPaymentCorrections.jsx";
import { BillHistorySheet } from "../../../shared/components/BillHistorySheet.jsx";
import { getInvoiceRevisions } from "../services/invoiceService.js";

/** Invoice detail as a sheet/dialog, or inline in the desktop split view (variant="panel"). */
export function InvoiceDetailModal({ isOpen, onClose, invoice, onRecordPayment, onDeleteInvoice, onPaymentsChanged, onEditInvoice, variant }) {
  const { confirmDanger } = useDangerConfirm();
  const [paymentAmount, setPaymentAmount] = useState("");
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [showPaymentBox, setShowPaymentBox] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showTallyModal, setShowTallyModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedReceiptPayment, setSelectedReceiptPayment] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Edit / void / reset payments recorded against this invoice (closes the sheet so fresh data loads)
  const paymentCorrections = useBillPaymentCorrections({
    billType: "invoice",
    bill: invoice,
    onChanged: async () => {
      if (onPaymentsChanged) await onPaymentsChanged();
      onClose();
    }
  });

  if (!invoice) return null;

  const isNonGst = invoice.invoiceType === "Normal";
  const billTitle = isNonGst ? "Bill of Supply / Cash Memo" : "GST Tax Invoice";
  const statusVariant = invoice.paymentStatus === "Paid" ? "success" : (invoice.paymentStatus === "Partial" ? "warning" : "danger");
  const isPaid = invoice.paymentStatus === "Paid";
  const balance = Number(invoice.balance || 0);

  const issuingName = invoice.companyName || invoice.companyDetails?.name || invoice.sellerConfig?.name || "SREE RAM TRADERS";
  const bankInfo = invoice.companyDetails?.bankName || invoice.sellerConfig?.bankName;

  const companyDateFormat = invoice.companyDetails?.dateFormat || invoice.sellerConfig?.dateFormat || "DD-MM-YYYY";
  const formattedInvoiceDate = formatAppDate(invoice.invoiceDate, companyDateFormat);

  // Billing & Shipping Address Details
  const rawBillStreet = invoice.billingAddress?.addressLine1 || invoice.customerAddress || "";
  const billCity = invoice.billingAddress?.city || invoice.customerCity || "";
  const billState = invoice.billingAddress?.state || invoice.customerState || "";
  const billPincode = invoice.billingAddress?.pincode || "";
  const billStreet = rawBillStreet.trim().toLowerCase() === billCity.trim().toLowerCase() ? "" : rawBillStreet;
  const billCityState = [
    billCity,
    billState ? (billPincode ? `${billState} - ${billPincode}` : billState) : billPincode
  ].filter(Boolean).join(", ");

  const rawShipStreet = invoice.shippingAddress?.addressLine1 || rawBillStreet;
  const shipCity = invoice.shippingAddress?.city || billCity;
  const shipState = invoice.shippingAddress?.state || billState;
  const shipPincode = invoice.shippingAddress?.pincode || billPincode;
  const shipStreet = rawShipStreet.trim().toLowerCase() === shipCity.trim().toLowerCase() ? "" : rawShipStreet;
  const shipCityState = [
    shipCity,
    shipState ? (shipPincode ? `${shipState} - ${shipPincode}` : shipState) : shipPincode
  ].filter(Boolean).join(", ");

  const shipLabel = invoice.shippingAddress?.label;
  const hasCustomShipLabel = shipLabel && shipLabel !== "Billing Address" && shipLabel !== "Primary Destination";

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(paymentAmount || 0);
    if (amt <= 0) return;

    setRecordingPayment(true);
    try {
      await onRecordPayment(invoice.id, {
        paidAmount: amt,
        customerId: invoice.customerId
      });
      setShowPaymentBox(false);
      setPaymentAmount("");
      onClose();
    } catch (err) {
      alert(err.message || "Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeleteClick = async () => {
    const confirmMsg = `Are you sure you want to delete Invoice #${invoice.invoiceNo} for ${invoice.customerName}?\n\n⚠️ This will RESTORE the inventory stock for all items in this bill and adjust customer balance.`;
    if (!(await confirmDanger({ title: `Delete invoice ${invoice.invoiceNo}?`, message: confirmMsg }))) return;

    setDeleting(true);
    try {
      if (onDeleteInvoice) {
        await onDeleteInvoice(invoice.id);
      }
      onClose();
    } catch (err) {
      alert(err.message || "Failed to delete invoice");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} variant={variant} title={`${billTitle} • ${invoice.invoiceNo}`}>
        <div className="invoice-detail-view" style={{ fontSize: "0.85rem" }}>
          {/* Header Summary */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1rem",
          paddingBottom: "0.75rem",
          borderBottom: "1px solid var(--border-subtle)"
        }}>
          <div>
            <div style={{ fontSize: "1.05rem", fontWeight: "700", color: "var(--text-main)" }}>
              {invoice.companyName || invoice.companyDetails?.name || invoice.sellerConfig?.name || "SREE RAM TRADERS"}
            </div>
            {!isNonGst && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
                GSTIN: {invoice.companyGstin || invoice.companyDetails?.gstin || invoice.sellerConfig?.gstin || "Unregistered"}
              </div>
            )}
            <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", marginTop: "2px" }}>
              Date: {formattedInvoiceDate} • Type: <strong style={{ color: isNonGst ? "var(--warning)" : "var(--primary)" }}>{isNonGst ? "Non-GST" : "GST Bill"}</strong>
            </div>
            {invoice.lorryNo && (
              <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: "2px" }}>
                🚚 Lorry No: <strong>{invoice.lorryNo}</strong>
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <Badge variant={statusVariant}>
              {invoice.paymentStatus || "Unpaid"}
            </Badge>
            <div style={{ fontSize: "1.2rem", fontWeight: "800", color: "var(--accent-blue)", marginTop: "4px" }}>
              ₹{Number(invoice.grandTotal || 0).toLocaleString("en-IN")}
            </div>
            {Number(invoice.editCount || 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                title="See earlier versions"
                style={{ marginTop: "4px", fontSize: "0.72rem", fontWeight: 600, padding: "1px 8px", borderRadius: 10, cursor: "pointer", background: "rgba(245, 158, 11, 0.12)", color: "var(--warning, #b45309)", border: "1px solid rgba(245, 158, 11, 0.4)" }}
              >
                ✏️ Edited {invoice.editCount}×
              </button>
            )}
          </div>
        </div>

        {/* Customer & Shipping Addresses */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "0.75rem",
          background: "var(--bg-card)",
          padding: "0.75rem",
          borderRadius: "var(--radius-md)",
          marginBottom: "1rem"
        }}>
          <div>
            <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Billed To</div>
            <div style={{ fontWeight: "700", marginTop: "2px" }}>{invoice.customerName}</div>
            {billStreet && (
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                {billStreet}
              </div>
            )}
            <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
              {billCityState || `${invoice.customerCity}, ${invoice.customerState}`}
            </div>
            {!isNonGst && invoice.customerGstin && (
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                GSTIN: <strong>{invoice.customerGstin}</strong>
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: "0.7rem", color: "var(--text-dim)", textTransform: "uppercase" }}>Shipped Destination</div>
            <div style={{ fontWeight: "700", marginTop: "2px" }}>
              {invoice.customerName}
              {hasCustomShipLabel && (
                <span style={{ fontSize: "0.85em", fontWeight: "normal", color: "var(--text-dim)" }}>
                  {" "}({shipLabel})
                </span>
              )}
            </div>
            {shipStreet && (
              <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                {shipStreet}
              </div>
            )}
            <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "2px" }}>
              {shipCityState || `${invoice.customerCity}, ${invoice.customerState}`}
            </div>
            {invoice.lorryNo && (
              <div style={{ fontSize: "0.72rem", color: "#38bdf8", marginTop: "3px", fontWeight: "600" }}>
                🚛 Lorry: <strong>{invoice.lorryNo}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Line Items Table */}
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: "0.5rem" }}>
            Itemized Details
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            {(invoice.lineItems || []).map((li, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.6rem 0.75rem",
                  background: "var(--bg-card)",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid var(--border-subtle)"
                }}
              >
                <div>
                  <div style={{ fontWeight: "600" }}>{li.itemName}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-dim)" }}>
                    {li.qty} {li.unit || "pcs"} × ₹{li.rate} {!isNonGst && `(${li.gstPercent}% GST)`}
                  </div>
                </div>
                <div style={{ fontWeight: "700" }}>
                  ₹{Number(li.lineTotal).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tax Breakdown & Settlement */}
        <div style={{
          background: "var(--bg-card)",
          padding: "0.75rem",
          borderRadius: "var(--radius-md)",
          marginBottom: "1rem"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
            <span style={{ color: "var(--text-muted)" }}>Item Subtotal:</span>
            <span>₹{Number(invoice.subtotal || 0).toLocaleString("en-IN")}</span>
          </div>

          {!isNonGst ? (
            invoice.isInterState ? (
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                <span style={{ color: "var(--text-muted)" }}>IGST (Integrated Tax):</span>
                <span>₹{Number(invoice.igst || 0).toLocaleString("en-IN")}</span>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                  <span style={{ color: "var(--text-muted)" }}>CGST (Central Tax):</span>
                  <span>₹{Number(invoice.cgst || 0).toLocaleString("en-IN")}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
                  <span style={{ color: "var(--text-muted)" }}>SGST (State Tax):</span>
                  <span>₹{Number(invoice.sgst || 0).toLocaleString("en-IN")}</span>
                </div>
              </>
            )
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem", color: "var(--text-dim)" }}>
              <span>Tax Classification:</span>
              <span>Non-GST (0%)</span>
            </div>
          )}

          {invoice.roundOff && Number(invoice.roundOff) !== 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
              <span style={{ color: "var(--text-muted)" }}>Round Off:</span>
              <span>₹{invoice.roundOff}</span>
            </div>
          )}

          <div style={{ borderTop: "1px solid var(--border-subtle)", margin: "0.5rem 0" }} />

          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700" }}>
            <span>Paid to Date:</span>
            <span style={{ color: "var(--success)" }}>₹{Number(invoice.paid || 0).toLocaleString("en-IN")}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "700", marginTop: "2px" }}>
            <span>Remaining Balance:</span>
            <span style={{ color: balance > 0 ? "var(--warning)" : "var(--text-dim)" }}>
              ₹{balance.toLocaleString("en-IN")}
            </span>
          </div>
        </div>

        {/* Bank & Remittance Details */}
        {(invoice.companyDetails?.bankName || invoice.sellerConfig?.bankName) && (
          <div style={{
            background: "var(--bg-card)",
            padding: "0.75rem",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-subtle)",
            marginBottom: "1rem",
            fontSize: "0.78rem"
          }}>
            <div style={{ fontWeight: "700", color: "#38bdf8", textTransform: "uppercase", marginBottom: "4px" }}>
              🏦 Bank Remittance Details
            </div>
            <div>Bank: <strong>{invoice.companyDetails?.bankName || invoice.sellerConfig?.bankName}</strong> {invoice.companyDetails?.branchName ? `(${invoice.companyDetails.branchName})` : ""}</div>
            <div>A/C #: <strong>{invoice.companyDetails?.accountNumber || invoice.sellerConfig?.accountNumber}</strong></div>
            <div>IFSC: <strong>{invoice.companyDetails?.ifscCode || invoice.sellerConfig?.ifscCode}</strong></div>
          </div>
        )}

        {/* Payment History Section */}
        {((invoice.paymentHistory || []).length > 0 || paymentCorrections.unrecordedAmount > 0) && (
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.72rem", fontWeight: "700", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.4rem" }}>
              💳 Payment History ({(invoice.paymentHistory || []).length})
            </div>
            <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
              {(invoice.paymentHistory || []).map((ph, idx) => (
                <div
                  key={ph.paymentId || idx}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.55rem 0.75rem",
                    borderBottom: idx < (invoice.paymentHistory || []).length - 1 ? "1px solid var(--border-subtle)" : "none",
                    background: "var(--bg-card)",
                    opacity: ph.voided ? 0.6 : 1
                  }}
                >
                  <div style={{ fontSize: "0.78rem", minWidth: 0 }}>
                    <div style={{ fontWeight: "600", color: "var(--text-main)" }}>
                      {ph.paymentNo || "Receipt"}
                      {ph.voided && <span style={{ marginLeft: "6px", color: "var(--danger)", fontWeight: "800", fontSize: "0.7rem" }}>VOID</span>}
                    </div>
                    <div style={{ color: "var(--text-dim)", marginTop: "1px" }}>
                      {formatAppDate(ph.paymentDate, companyDateFormat)} • {ph.paymentMode || "Cash"}
                      {ph.reference && ` • ${ph.reference}`}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                    <span style={{ fontWeight: "800", color: ph.voided ? "var(--text-dim)" : "var(--success)", fontSize: "0.9rem", textDecoration: ph.voided ? "line-through" : "none" }}>
                      +₹{Number(ph.amount || 0).toLocaleString("en-IN")}
                    </span>
                    {paymentCorrections.renderRowActions(ph)}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedReceiptPayment({
                        id: ph.paymentId,
                        paymentNo: ph.paymentNo,
                        type: "PAYMENT_IN",
                        amount: ph.amount,
                        paymentDate: ph.paymentDate,
                        paymentMode: ph.paymentMode,
                        reference: ph.reference,
                        customerName: invoice.customerName,
                        customerPhone: invoice.customerPhone || "",
                        allocations: [{
                          invoiceId: invoice.id,
                          invoiceNo: invoice.invoiceNo,
                          invoiceDate: invoice.invoiceDate,
                          grandTotal: invoice.grandTotal,
                          allocatedAmount: ph.amount,
                          remainingBalance: 0
                        }]
                      })}
                      style={{ fontSize: "0.7rem", padding: "2px 7px" }}
                    >
                      📄 Receipt
                    </Button>
                  </div>
                </div>
              ))}
              {paymentCorrections.unrecordedRow}
            </div>
          </div>
        )}

        {/* Payment Box (If unpaid/partial) */}
        {showPaymentBox && (
          <form onSubmit={handlePaymentSubmit} style={{ marginBottom: "1rem", background: "var(--bg-card)", padding: "0.85rem", borderRadius: "var(--radius-md)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <span style={{ fontWeight: "600" }}>Record Payment Received</span>
              <button
                type="button"
                onClick={() => setShowTallyModal(true)}
                style={{ background: "none", border: "none", color: "#38bdf8", fontSize: "0.75rem", cursor: "pointer", textDecoration: "underline", padding: 0 }}
              >
                💰 Settle multiple invoices
              </button>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                type="number"
                step="0.01"
                placeholder={`Max: ₹${balance}`}
                max={balance}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="form-control"
                required
              />
              <Button type="submit" variant="primary" loading={recordingPayment} size="sm">
                Confirm
              </Button>
            </div>
          </form>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {!isPaid && !showPaymentBox && (
            <Button
              type="button"
              variant="secondary"
              style={{ flex: 1, minHeight: "40px" }}
              onClick={() => {
                setPaymentAmount(balance.toString());
                setShowPaymentBox(true);
              }}
            >
              💳 Record Payment
            </Button>
          )}

          {onEditInvoice && (
            <Button
              type="button"
              variant="secondary"
              style={{ flex: 1, minHeight: "40px" }}
              onClick={() => onEditInvoice(invoice)}
            >
              ✏️ Edit
            </Button>
          )}

          <Button
            type="button"
            variant="primary"
            style={{ flex: 1, minHeight: "40px" }}
            onClick={() => setShowPrintModal(true)}
          >
            🖨️ Print / PDF
          </Button>

          <Button
            type="button"
            variant="danger"
            style={{ minHeight: "40px" }}
            loading={deleting}
            onClick={handleDeleteClick}
          >
            🗑️ Delete
          </Button>
        </div>
      </div>
    </BottomSheet>

    <BillHistorySheet
      isOpen={showHistory}
      onClose={() => setShowHistory(false)}
      bill={invoice}
      billNo={invoice.invoiceNo}
      loadRevisions={getInvoiceRevisions}
      renderOld={(old, close) => <InvoicePrintModal isOpen onClose={close} invoice={old} watermark="OLD VERSION" />}
      dateFormat={invoice.companyDetails?.dateFormat || "DD-MM-YYYY"}
    />

    {/* PDF / Physical Print Preview Modal */}
    <InvoicePrintModal
      isOpen={showPrintModal}
      onClose={() => setShowPrintModal(false)}
      invoice={invoice}
    />

    {/* Multi-Invoice Payment Tally Modal */}
    <PaymentTallyModal
      isOpen={showTallyModal}
      onClose={() => setShowTallyModal(false)}
      preselectedCustomer={invoice.customerId ? { id: invoice.customerId, name: invoice.customerName } : null}
      onPaymentSaved={() => {
        setShowPaymentBox(false);
        onClose();
      }}
    />

    {/* Payment Receipt Modal (from payment history rows) */}
    {paymentCorrections.host}

    <PaymentReceiptModal
      isOpen={!!selectedReceiptPayment}
      onClose={() => setSelectedReceiptPayment(null)}
      payment={selectedReceiptPayment}
    />
  </>
  );
}

export default InvoiceDetailModal;
